/**
 * Слайдер-полка: горизонтальный ряд карточек с мягкими краями и кнопками.
 *
 * Зачем. Полки блоков были голым `overflow-x`: карточки обрывались по краю
 * жёсткой линией, а на мыши без горизонтального колеса их нечем было листать
 * (владелец 14.09: «нужны полки с прокруткой, но доработанные в понятные
 * слайдеры с мягкими краями и кнопками»). Один компонент на все полки:
 * блоки разделов и ленты трендов — принцип у них общий.
 *
 * Края гасим маской и ТОЛЬКО с той стороны, где ещё есть карточки: постоянная
 * маска с обеих сторон съедала бы первую и последнюю карточку в упор.
 * Кнопки по той же причине живут по состоянию, а не всегда: доехал до края —
 * кнопка уходит, иначе она обещает прокрутку, которой нет.
 *
 * Жесты (колесо, протяжка мышью, глушение клика после протяжки) остаются в
 * DragScrollRow — здесь только края, кнопки и снап.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import DragScrollRow from './DragScrollRow'

interface Props {
	children: ReactNode
	/** Класс на сам ряд — вёрстка полки (гэп, размеры плиток) остаётся за страницей. */
	rowClassName?: string
	className?: string
	/** Подпись для кнопок: «Листать влево/вправо» + это. */
	label?: string
}

/** Запас в пикселях: ближе этого к краю считаем, что доехали (дробный scrollLeft). */
const EDGE = 2

export default function SliderRow({ children, rowClassName, className, label }: Props) {
	const rowRef = useRef<HTMLDivElement>(null)
	const [edges, setEdges] = useState<{ left: boolean; right: boolean }>({ left: false, right: false })

	const measure = useCallback(() => {
		const el = rowRef.current
		if (!el) return
		const max = el.scrollWidth - el.clientWidth
		setEdges({ left: el.scrollLeft > EDGE, right: max > EDGE && el.scrollLeft < max - EDGE })
	}, [])

	useEffect(() => {
		const el = rowRef.current
		if (!el) return
		measure()
		el.addEventListener('scroll', measure, { passive: true })
		// Ряд меняет ширину и от окна, и от состава (карточки приезжают позже
		// первого рендера) — слушаем оба, иначе кнопки застывают в первом замере.
		const ro = new ResizeObserver(measure)
		ro.observe(el)
		for (const kid of Array.from(el.children)) ro.observe(kid)
		return () => {
			el.removeEventListener('scroll', measure)
			ro.disconnect()
		}
	}, [measure, children])

	const page = (dir: -1 | 1) => {
		const el = rowRef.current
		if (!el) return
		// Шаг — почти экран ряда: целые карточки уезжают, край остаётся виден,
		// и человек не теряет место, на котором остановился.
		el.scrollBy({ left: dir * Math.max(120, el.clientWidth * 0.85), behavior: 'smooth' })
	}

	const fade = edges.left && edges.right ? 'both' : edges.left ? 'left' : edges.right ? 'right' : 'none'

	return (
		<div className={`slider-row${className ? ` ${className}` : ''}`} data-fade={fade}>
			<DragScrollRow ref={rowRef} className={`slider-row__track${rowClassName ? ` ${rowClassName}` : ''}`}>
				{children}
			</DragScrollRow>
			{edges.left && (
				<button
					type="button"
					className="slider-row__nav slider-row__nav--prev"
					aria-label={label ? `Листать влево: ${label}` : 'Листать влево'}
					onClick={() => page(-1)}
				>
					<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
						<path d="M15 18l-6-6 6-6" />
					</svg>
				</button>
			)}
			{edges.right && (
				<button
					type="button"
					className="slider-row__nav slider-row__nav--next"
					aria-label={label ? `Листать вправо: ${label}` : 'Листать вправо'}
					onClick={() => page(1)}
				>
					<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
						<path d="M9 18l6-6-6-6" />
					</svg>
				</button>
			)}
		</div>
	)
}
